import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';

/**
 * KPICard - Carte de statistique avec icône + label + valeur
 *
 * Affiche un indicateur clé avec formatage personnalisé.
 * Supporte un lien optionnel vers une autre page.
 *
 * @param {Object} props
 * @param {Component} props.icon - Composant icône Lucide
 * @param {string} props.iconColor - Couleur de l'icône
 * @param {string} props.bgColor - Couleur de fond de la carte
 * @param {string} props.borderColor - Couleur de la bordure
 * @param {string} props.label - Texte du label
 * @param {string} props.labelColor - Couleur du label
 * @param {string|number} props.value - Valeur affichée
 * @param {string} props.valueColor - Couleur de la valeur
 * @param {number} [props.iconSize=32] - Taille de l'icône
 * @param {string} [props.linkTo] - URL pour Link (optionnel)
 * @param {boolean} [props.showExternalIcon=false] - Afficher icône ExternalLink
 */
function KPICard({
  icon: Icon,
  iconColor,
  bgColor,
  borderColor,
  label,
  labelColor,
  value,
  valueColor,
  iconSize = 32,
  linkTo,
  showExternalIcon = false
}) {
  const cardStyle = {
    backgroundColor: bgColor,
    border: `2px solid ${borderColor}`,
    borderRadius: '0.75rem',
    padding: '1.25rem',
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    position: 'relative',
    transition: 'all 200ms',
    textDecoration: 'none'
  };

  const cardContent = (
    <>
      <Icon size={iconSize} color={iconColor} />
      <div>
        <div style={{
          fontSize: '1rem',
          color: labelColor,
          marginBottom: '0.25rem',
          fontFamily: '"Noto Sans", sans-serif'
        }}>
          {label}
        </div>
        <div style={{
          fontSize: '2rem',
          fontWeight: 'bold',
          color: valueColor,
          fontFamily: '"Noto Sans", sans-serif'
        }}>
          {value}
        </div>
      </div>
      {showExternalIcon && (
        <ExternalLink
          size={18}
          style={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            color: borderColor
          }}
        />
      )}
    </>
  );

  if (linkTo) {
    return (
      <Link
        to={linkTo}
        style={cardStyle}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-4px)';
          e.currentTarget.style.boxShadow = '0 10px 25px rgba(0,0,0,0.15)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        {cardContent}
      </Link>
    );
  }

  return <div style={cardStyle}>{cardContent}</div>;
}

export default KPICard;
